import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, Unplug, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import { apiClient } from '@/infrastructure/api/client'

// ──────────────────────────────────────────────
// Twitch token-status shape (server response)
// ──────────────────────────────────────────────
interface TwitchTokenStatus {
  isConnected: boolean
  botUsername?: string
  expiresAt?: string
  expiresInMinutes?: number
  needsReauth: boolean
  scopes?: string[]
  language?: 'en' | 'fr'
}

/**
 * Twitch bot connection panel: shows live token status, connect/disconnect
 * actions, configured scopes and the bot's response language selector.
 */
export function TwitchBotPanel() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<TwitchTokenStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [revoking, setRevoking] = useState(false)
  const [connecting, setConnecting] = useState(false)

  const fetchStatus = useCallback(async () => {
    try {
      const result = await apiClient.get<TwitchTokenStatus>('/auth/twitch/status')
      setStatus(result.data)
    } catch {
      setStatus({ isConnected: false, needsReauth: true })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const result = await apiClient.get<{ url: string }>('/auth/twitch/url')
      window.location.href = result.data.url
    } catch (error: unknown) {
      toast.error(
        `Could not start OAuth flow: ${error instanceof Error ? error.message : String(error)}`,
      )
      setConnecting(false)
    }
  }

  const handleRevoke = async () => {
    if (!window.confirm('Disconnect the bot and revoke its Twitch tokens?')) return
    setRevoking(true)
    try {
      await apiClient.delete('/auth/twitch')
      toast.success('Bot disconnected.')
      await fetchStatus()
    } catch (error: unknown) {
      toast.error(`Failed to revoke: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setRevoking(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin" />
        {t('admin.twitch_checking')}
      </div>
    )
  }

  const isOk = status?.isConnected && !status.needsReauth

  return (
    <div className="space-y-4">
      {/* Status row */}
      <div className="flex flex-wrap items-center gap-3">
        {isOk ? (
          <>
            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" />
            <span className="text-sm font-medium">
              {t('admin.twitch_connected_as')}{' '}
              <span className="font-bold text-foreground">@{status!.botUsername}</span>
            </span>
            <Badge variant="secondary" className="text-xs">
              {t('admin.twitch_expires_in', { minutes: status!.expiresInMinutes })}
            </Badge>
          </>
        ) : status?.botUsername ? (
          <>
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
              {t('admin.twitch_expired', { username: status.botUsername })}
            </span>
          </>
        ) : (
          <>
            <XCircle className="w-5 h-5 text-muted-foreground shrink-0" />
            <span className="text-sm text-muted-foreground">{t('admin.twitch_not_connected')}</span>
          </>
        )}
      </div>

      {/* Scopes (only when connected) */}
      {isOk && status?.scopes && (
        <div className="flex flex-wrap gap-1">
          {status.scopes.map((scope) => (
            <Badge key={scope} variant="outline" className="text-xs font-mono">
              {scope}
            </Badge>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {isOk ? (
          <Button
            variant="destructive"
            size="sm"
            leftIcon={<Unplug className="w-4 h-4" />}
            onClick={handleRevoke}
            isLoading={revoking}
          >
            {t('admin.twitch_disconnect')}
          </Button>
        ) : (
          <Button
            size="sm"
            leftIcon={<ExternalLink className="w-4 h-4" />}
            onClick={handleConnect}
            isLoading={connecting}
          >
            {status?.needsReauth && status.botUsername
              ? t('admin.twitch_reauthenticate')
              : t('admin.twitch_connect')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={fetchStatus}
        >
          Refresh
        </Button>
      </div>

      {/* Help text */}
      {!isOk && (
        <p className="text-xs text-muted-foreground">
          You need a{' '}
          <a
            href="https://dev.twitch.tv/console/apps"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Twitch Developer Application
          </a>{' '}
          with <code className="text-xs bg-muted px-1 rounded">TWITCH_CLIENT_ID</code> and{' '}
          <code className="text-xs bg-muted px-1 rounded">TWITCH_CLIENT_SECRET</code> set in your{' '}
          <code className="text-xs bg-muted px-1 rounded">.env</code>. The bot will automatically
          refresh its token before expiry.
        </p>
      )}
    </div>
  )
}
